import { Card, CardContent, ToggleGroup, ToggleGroupItem } from '@ibsys/kit';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { STRATEGIST_CONCEPT_ROLE } from '@/entities/strategist';
import type { StrategistTargetAttribute } from '@/entities/strategist';
import { TargetMetricPicker } from '@/features/strategist-target';
import { ThresholdSlider } from '@/shared/ui';

/** Подписи известных категорий понятий; незнакомая категория показывается своим кодом. */
const CATEGORY_LABEL_KEYS = {
  [STRATEGIST_CONCEPT_ROLE.objection]: 'hierarchy.categories.возражение',
  [STRATEGIST_CONCEPT_ROLE.argument]: 'hierarchy.categories.аргумент',
  [STRATEGIST_CONCEPT_ROLE.question]: 'hierarchy.categories.вопрос',
} as const;

type Props = {
  /** Текущая целевая метрика */
  target: StrategistTargetAttribute;
  /** Смена целевой метрики */
  onTargetChange: (next: StrategistTargetAttribute) => void;
  /** Доступные категории (роли концептов) из ответа */
  categories: string[];
  /** Текущая категория среза */
  category: string | undefined;
  onCategoryChange: (value: string) => void;
  /** Доступные пороги близости (шаги ползунка) */
  thresholds: number[];
  /** Текущий порог близости */
  threshold: number | undefined;
  onThresholdChange: (value: number) => void;
};

/**
 * Управление страницы «Структурные единицы»: целевая метрика, категория понятий и
 * порог близости.
 */
export const HierarchyControls: FC<Props> = ({
  target,
  onTargetChange,
  categories,
  category,
  onCategoryChange,
  thresholds,
  threshold,
  onThresholdChange,
}) => {
  const { t } = useTranslation('strategist');

  return (
    <Card>
      <CardContent className="p-4 flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end lg:gap-8">
        <TargetMetricPicker value={target} onChange={onTargetChange} />

        <div className="flex flex-col gap-2">
          <span className="text-xs text-muted-foreground">{t('hierarchy.category.label')}</span>
          <ToggleGroup value={category ?? ''} onValueChange={(v: string) => v && onCategoryChange(v)}>
            {categories.map((item) => {
              const key = CATEGORY_LABEL_KEYS[item as keyof typeof CATEGORY_LABEL_KEYS];
              return (
                <ToggleGroupItem key={item} value={item} className="!flex-none whitespace-nowrap px-4">
                  {key ? t(key) : item}
                </ToggleGroupItem>
              );
            })}
          </ToggleGroup>
        </div>

        <ThresholdSlider
          label={t('hierarchy.threshold.label')}
          thresholds={thresholds}
          value={threshold}
          onChange={onThresholdChange}
          coarserLabel={t('hierarchy.threshold.coarser')}
          finerLabel={t('hierarchy.threshold.finer')}
          className="flex flex-col gap-2 lg:w-80"
        />
      </CardContent>
    </Card>
  );
};
